import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { frameworkBySlug } from "@/content/en/frameworks";
import type { Framework } from "@/content/en/types";
import { conceptBySlug } from "@/content/en/concepts";
import { ideaBySlug } from "@/content/en/ideas";
import { CitationBox, BriefingCTA, TopicList } from "@/components/en/EnPrimitives";
import { MachineSummary } from "@/components/en/MachineSummary";
import {
  personSchema,
  breadcrumbSchema,
  enHreflangLinks,
  PERSON_ID,
  EN_HOME,
  SITE_URL,
} from "@/lib/en-schema";
import { pageTitle } from "@/lib/brand";


export const Route = createFileRoute("/frameworks/$slug")({
  loader: ({ params }) => {
    const framework = frameworkBySlug(params.slug);
    if (!framework) throw notFound();
    return { framework };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: pageTitle("Framework not found", "en") }, { name: "robots", content: "noindex" }] };
    }
    const f = loaderData.framework;
    const canonical = `https://smartsustainability.ai/frameworks/${params.slug}`;
    return {
      meta: [
        { title: pageTitle(`${f.name} — Framework`, "en") },
        { name: "description", content: f.shortDescription },
        { property: "og:title", content: f.name },
        { property: "og:description", content: f.shortDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: f.name },
      { name: "twitter:description", content: f.shortDescription },
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...enHreflangLinks(canonical),
      ],

      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(personSchema()),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: f.name,
            headline: f.name,
            description: f.shortDescription,
            abstract: f.coreQuestion,
            creator: { "@id": PERSON_ID },
            author: { "@id": PERSON_ID },
            dateModified: f.updated,
            inLanguage: "en",
            keywords: f.topics.join(", "),
            url: canonical,
            mainEntityOfPage: canonical,
            genre: "Analytical framework",
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", item: EN_HOME },
              { name: "Frameworks", item: `${SITE_URL}/frameworks` },
              { name: f.name, item: canonical },
            ]),
          ),
        },
      ],
    };
  },

  component: FrameworkDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-20 text-center">
      <h1 className="font-serif text-3xl text-ink">Framework not found</h1>
      <Link to="/frameworks" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] text-clay underline underline-offset-4">
        Back to Frameworks
      </Link>
    </div>
  ),
});

function FrameworkDetail() {
  const { framework: f } = Route.useLoaderData() as { framework: Framework };
  return (
    <article className="mx-auto max-w-3xl px-6 py-16 md:py-20">
      <header className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.24em] text-clay">Framework</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight text-ink md:text-5xl">{f.name}</h1>
        <p className="mt-4 text-[15px] leading-relaxed text-foreground/80">
          {f.shortDescription}
        </p>
      </header>

      <MachineSummary
        kind="framework"
        slug={f.slug}
        summary={f.shortDescription}
        coreQuestion={f.coreQuestion}
        coreClaim={f.explanation.split(". ")[0] + "."}
        relatedConceptSlugs={f.relatedConcepts}
        relatedIdeaSlugs={f.relatedIdeas}
        topics={f.topics}
        updated={f.updated}
      />



      <section className="mb-10 rounded-md border-l-4 border-clay bg-clay/5 p-5">
        <p className="text-[10px] uppercase tracking-[0.22em] text-clay mb-2">Core question</p>
        <p className="font-serif text-xl text-ink leading-snug md:text-2xl">{f.coreQuestion}</p>
      </section>

      <section className="mb-10">
        <h2 className="mb-3 font-serif text-2xl text-ink">Model</h2>
        <p className="text-[16px] leading-[1.7] text-foreground/85">{f.explanation}</p>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 font-serif text-2xl text-ink">Components</h2>
        <ol className="space-y-4">
          {f.components.map((c, i) => (
            <li key={i} className="rounded-md border border-ink/10 bg-paper p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] text-clay">Component {i + 1}</p>
              <h3 className="mt-2 font-serif text-lg text-ink">{c.name}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-foreground/85">{c.description}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 font-serif text-2xl text-ink">Use cases</h2>
        <ul className="space-y-2 list-disc list-outside pl-6 text-[15px] leading-relaxed text-foreground/85">
          {f.useCases.map((u, i) => <li key={i}>{u}</li>)}
        </ul>
      </section>

      <section className="mb-10">
        <TopicList topics={f.topics} />
      </section>

      {f.relatedConcepts.length > 0 && (
        <section className="mb-8 rounded-md border border-ink/10 bg-paper p-6">
          <h2 className="mb-4 font-serif text-lg text-ink">Related concepts</h2>
          <ul className="space-y-3">
            {f.relatedConcepts.map((slug) => {
              const c = conceptBySlug(slug);
              if (!c) return null;
              return (
                <li key={slug}>
                  <Link to="/concepts/$slug" params={{ slug }} className="group block">
                    <p className="font-serif text-base text-ink group-hover:text-clay">{c.name}</p>
                    <p className="text-sm text-muted-foreground">{c.oneLine}</p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {f.relatedIdeas.length > 0 && (
        <section className="mb-8 rounded-md border border-ink/10 bg-paper p-6">
          <h2 className="mb-4 font-serif text-lg text-ink">Related ideas</h2>
          <ul className="space-y-3">
            {f.relatedIdeas.map((slug) => {
              const i = ideaBySlug(slug);
              if (!i) return null;
              return (
                <li key={slug}>
                  <Link to="/ideas/$slug" params={{ slug }} className="group block">
                    <p className="font-serif text-base text-ink group-hover:text-clay">{i.title}</p>
                    <p className="text-sm text-muted-foreground">{i.subtitle}</p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {f.references.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 font-serif text-xl text-ink">References</h2>
          <ul className="space-y-2 text-sm text-foreground/80">
            {f.references.map((r, i) => (
              <li key={i}>
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noopener" className="underline underline-offset-2 hover:text-clay">
                    {r.label}
                  </a>
                ) : r.label}
              </li>
            ))}
          </ul>
        </section>
      )}

      <CitationBox year={new Date(f.updated).getFullYear()} title={`${f.name} (Framework)`} path={`/frameworks/${f.slug}`} />

      <BriefingCTA variant="inline" />
    </article>
  );
}
